import { useContext, useMemo, useRef, useState } from 'react';
import UserContext from '../../contexts/UserContext';
import sendToast from '../../utils/Toast';
import showFavoritePageToast from '../../utils/Toast/showFavoritePageToast';
import { updateRelative } from '../../services/family';
import { DefaultNeighborsLanding, normalizeDefaultNeighborsLanding } from '../../types/userProfile';

const useDefaultNeighborsLanding = () => {
    const { userObject, setUser } = useContext(UserContext);
    const [isSaving, setIsSaving] = useState(false);
    const isSavingRef = useRef(false);

    const activeRegistrantsIndexes = userObject?.registrants?.activeRegistrantsIndexes || [];
    const listOfResidents = userObject?.registrants?.list || [];
    const activeRegistrantIndex = activeRegistrantsIndexes[0];

    const activeResident = useMemo(() => {
        if (typeof activeRegistrantIndex === 'number' && listOfResidents[activeRegistrantIndex]) {
            return listOfResidents[activeRegistrantIndex];
        }
        return listOfResidents[0];
    }, [activeRegistrantIndex, listOfResidents]);

    const defaultLanding = normalizeDefaultNeighborsLanding(userObject?.profile?.defaultNeighborsLanding);
    const isSocialDefault = defaultLanding === 'social';

    const setDefaultLanding = async (nextLanding: DefaultNeighborsLanding): Promise<boolean> => {
        if (isSavingRef.current || defaultLanding === nextLanding) {
            return true;
        }

        const residentId = activeResident?._id;
        const relativeId = userObject?.profile?._id;

        if (!residentId || !relativeId) {
            sendToast('error', 'Unable to update your sign-in page right now.');
            return false;
        }

        isSavingRef.current = true;
        setIsSaving(true);
        try {
            await updateRelative(residentId, relativeId, {
                defaultNeighborsLanding: nextLanding,
                FirstName: userObject?.profile?.FirstName,
                LastName: userObject?.profile?.LastName,
            });

            setUser({
                ...userObject,
                profile: {
                    ...userObject.profile,
                    defaultNeighborsLanding: nextLanding,
                },
            });

            if (nextLanding === 'social') {
                showFavoritePageToast('Social');
            } else {
                sendToast('success', 'Social is no longer your default page');
            }

            return true;
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to update your sign-in page.');
            return false;
        } finally {
            isSavingRef.current = false;
            setIsSaving(false);
        }
    };

    const toggleSocialDefault = async (): Promise<boolean> => {
        return setDefaultLanding(isSocialDefault ? 'home' : 'social');
    };

    return {
        defaultLanding,
        isSocialDefault,
        isSaving,
        setDefaultLanding,
        toggleSocialDefault,
    };
};

export default useDefaultNeighborsLanding;
